import { Link } from "react-router-dom";
import { MapPin, PhoneCall, HeartHandshake, BedDouble } from "lucide-react";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import CtaSection from "@/components/CtaSection";
import Seo from "@/components/Seo";
import ZoneMap, { ZONE_COLORS } from "@/components/ZoneMap";
import { CITIES, ZONES } from "@/data/cities";

const LOCAL_POINTS = [
    { icon: HeartHandshake, title: "On s'adapte à vous", desc: "Horaires, discrétion, contraintes familiales ou locatives : chaque intervention est adaptée à votre situation, pas l'inverse." },
    { icon: PhoneCall, title: "Un interlocuteur unique", desc: "Du premier appel au suivi après traitement, vous échangez avec la même équipe, joignable 7j/7 de 9h à 20h." },
    { icon: MapPin, title: "On connaît votre secteur", desc: "Basés au Mont-Dol, nous connaissons chaque commune de notre rayon d'action, sans majoration en semaine (samedis, dimanches et jours fériés : +20 %)." },
];

const ZoneIntervention = () => {
    return (
        <div data-testid="zone-page">
            <Seo
                title="Zone d'intervention | Plus de 150 communes autour du Mont-Dol | Eradikbreizh"
                description="Eradikbreizh intervient contre les punaises de lit dans plus de 150 communes : Côtes-d'Armor (22), Ille-et-Vilaine (35), Manche (50). Intervention sous 2h, 7j/7. Devis gratuit."
            />

            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-b from-mist to-white pb-16 pt-40 md:pt-48">
                <div className="absolute -right-40 top-0 h-[30rem] w-[30rem] rounded-full bg-brand/10 blur-[160px]" aria-hidden="true" />
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <MaskedLine delay={0.1}><SectionTag>Petite entreprise locale</SectionTag></MaskedLine>
                    <h1 className="mt-6 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-7xl">
                        <MaskedLine delay={0.25}>Zone d'intervention</MaskedLine>
                        <MaskedLine delay={0.4}>
                            <span className="text-brand">80 km autour du Mont-Dol</span>
                        </MaskedLine>
                    </h1>
                    <MaskedLine delay={0.55}>
                        <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                            Pas de plateforme ni de sous-traitance : vous parlez directement à l'équipe qui intervient
                            chez vous. Cliquez sur votre commune pour découvrir notre page dédiée aux punaises de lit.
                        </p>
                    </MaskedLine>
                    <div className="mt-12 grid gap-6 sm:grid-cols-3">
                        {LOCAL_POINTS.map((p, i) => (
                            <Reveal key={p.title} delay={0.08 * i}>
                                <div className="flex items-start gap-4 rounded-3xl border border-black/10 bg-white p-6 shadow-sm">
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                        <p.icon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="font-display font-bold">{p.title}</p>
                                        <p className="mt-1 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CARTE DES POINTS DE PRÉSENCE */}
            <section className="bg-mist py-20">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Carte d'intervention</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            {CITIES.length} points de présence <span className="text-brand">autour du Mont-Dol</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
                            Chaque point correspond à une commune où nous intervenons contre les punaises de lit. Cliquez sur un point pour ouvrir la page dédiée à votre ville.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2} className="mt-10">
                        <ZoneMap />
                    </Reveal>
                    <Reveal delay={0.25} className="mt-8 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-semibold text-night shadow-sm">
                            <span className="block h-3.5 w-3.5 rounded-full border-2 border-white bg-[#1B43BD] shadow" />
                            Base : Le Mont-Dol
                        </span>
                        {ZONES.map((z) => (
                            <span
                                key={z.id}
                                data-testid={`zone-legend-${z.id}`}
                                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-semibold text-night shadow-sm"
                            >
                                <span className="block h-3 w-3 rounded-full border-2 border-white shadow" style={{ backgroundColor: ZONE_COLORS[z.id] }} />
                                {z.label}
                            </span>
                        ))}
                    </Reveal>
                </div>
            </section>

            {/* COMMUNES PAR SECTEUR */}
            <section className="bg-white py-20">
                <div className="mx-auto max-w-7xl space-y-14 px-5 sm:px-8">
                    {ZONES.map((z) => (
                        <Reveal key={z.id}>
                            <h2 className="font-display text-2xl font-black uppercase tracking-tight text-brand sm:text-3xl">{z.label}</h2>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {CITIES.filter((c) => c.zone === z.id).map((c) => (
                                    <Link
                                        key={c.slug}
                                        data-testid={`zone-city-${c.slug}`}
                                        to={`/punaises-de-lit/${c.slug}`}
                                        className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-semibold text-night shadow-sm transition-[border-color,color] duration-300 hover:border-brand hover:text-brand"
                                    >
                                        <BedDouble className="h-3.5 w-3.5 text-brand" />
                                        {c.name}
                                    </Link>
                                ))}
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <CtaSection
                title="Votre commune est dans notre rayon ?"
                subtitle="Appelez-nous : intervention possible sous 2h, 7j/7 de 9h à 20h (samedis, dimanches et jours fériés : +20 %)."
            />
        </div>
    );
};

export default ZoneIntervention;
